import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";


export type RecurrenceFrequency = "choose" | "daily" | "weekly" | "monthly" | "yearly";
export type EndCondition = "after" | "on_date";
export type WeekLabel =
  | "first"
  | "second"
  | "third"
  | "fourth"
  | "last";

export interface MonthlyPattern {
  week: WeekLabel;
  day: string; // "Monday", "Tuesday", …
}

export interface RecurrenceConfig {
  enabled: boolean;
  frequency: RecurrenceFrequency;
  interval: number;
  weekDays: string[];               // ["Mon", "Tue", …]
  monthlyType: "day" | "pattern";
  monthlyDay: number;               // 1–31
  monthlyPattern: MonthlyPattern;
  endCondition: EndCondition;
  occurrences: number;
  endDate: string;                  // "yyyy-MM-dd"
}

interface RecurrenceSelectorProps {
  value?: Partial<RecurrenceConfig>;
  onChange?: (config: RecurrenceConfig) => void;
  startDate?: string;
}


const SHORT_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const FULL_DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const WEEK_LABELS: { value: WeekLabel; label: string }[] = [
  { value: "first", label: "First" },
  { value: "second", label: "Second" },
  { value: "third", label: "Third" },
  { value: "fourth", label: "Fourth" },
  { value: "last", label: "Last" },
];

const DEFAULT_CONFIG: RecurrenceConfig = {
  enabled: false,
  frequency: "choose",
  interval: 1,
  weekDays: ["Mon"],
  monthlyType: "day",
  monthlyDay: 1,
  monthlyPattern: { week: "first", day: "Monday" },
  endCondition: "after",
  occurrences: 1,
  endDate: "",
};


const RecurrenceSelector = ({ value, onChange, startDate }: RecurrenceSelectorProps) => {
  const [config, setConfig] = useState<RecurrenceConfig>({
    ...DEFAULT_CONFIG,
    ...value,
  });

  useEffect(() => {
  if (value && Object.keys(value).length > 0) {
    setConfig({
      ...DEFAULT_CONFIG,
      ...value,
      monthlyPattern: {
        ...DEFAULT_CONFIG.monthlyPattern,
        ...(value.monthlyPattern ?? {}),
      },
    });
  }
}, [JSON.stringify(value)]);

  const update = (patch: Partial<RecurrenceConfig>) => {
    const next = { ...config, ...patch };
    setConfig(next);
    onChange?.(next);
  };

  const toggleDay = (day: string) => {
    const days = config.weekDays.includes(day)
      ? config.weekDays.filter((d) => d !== day)
      : [...config.weekDays, day];
    update({ weekDays: days });
  };

  const frequencyLabel = {
    choose: "",
    daily: "day(s)",
    weekly: "week(s)",
    monthly: "month(s)",
    yearly: "year(s)",
  }[config.frequency];

  return (
    <Card className="p-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <p className="font-medium text-sm">Recurrence <span className="text-red-500">*</span></p>
      </div>
      <Separator className="my-3" />

      <div className="space-y-5">
        {/* ── Row 1: Frequency + Interval ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Frequency */}
          <div>
            <p className="text-sm mb-1">Frequency</p>
            <Select
              value={config.frequency}
              onValueChange={(v) =>
                update({ frequency: v as RecurrenceFrequency })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="choose">Choose frequency ...</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Interval */}
          {config.frequency !== "choose" && (
            <div>
              <p className="text-sm mb-1">
                Interval
              </p>
              <Input
                type="number"
                min={1}
                value={config.interval}
                onChange={(e) =>
                  update({ interval: Math.max(1, Number(e.target.value)) })
                }
                placeholder="1"
              />
            </div>
          )}
        </div>

        {/* ── Conditional sections ── */}
        {config.frequency !== "choose" && (
          <>
            {/* ── Weekly: day-of-week selector ── */}
            {config.frequency === "weekly" && (
              <div>
                <p className="text-sm mb-2">Repeat on</p>
                <div className="flex flex-wrap gap-2">
                  {SHORT_DAYS.map((day) => {
                    const active = config.weekDays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={cn(
                          "w-9 h-9 rounded-full text-xs font-medium border transition-colors",
                          active
                            ? "bg-primary text-white border-primary"
                            : "bg-white text-gray-600 border-gray-300 hover:border-primary"
                        )}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Monthly options ── */}
            {config.frequency === "monthly" && (
              <div className="space-y-3">
                <p className="text-sm">Monthly on</p>
                <div className="flex flex-col gap-3 items-start">
                  {/* Option A: On specific day */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="monthly-type"
                      checked={config.monthlyType === "day"}
                      onChange={() => update({ monthlyType: "day" })}
                      className="accent-primary"
                    />
                    <span className="text-sm">On day</span>
                    <Input
                      type="number"
                      min={1}
                      max={31}
                      disabled={config.monthlyType !== "day"}
                      value={config.monthlyDay}
                      onChange={(e) =>
                        update({
                          monthlyDay: Math.min(
                            31,
                            Math.max(1, Number(e.target.value))
                          ),
                        })
                      }
                      className="w-20 h-8 text-sm"
                    />
                  </label>

                  {/* Option B: On pattern */}
                  <label className="flex items-center gap-3 cursor-pointer flex-wrap">
                    <input
                      type="radio"
                      name="monthly-type"
                      checked={config.monthlyType === "pattern"}
                      onChange={() => update({ monthlyType: "pattern" })}
                      className="accent-primary"
                    />
                    <span className="text-sm">On the</span>
                    <Select
                      value={config.monthlyPattern.week}
                      disabled={config.monthlyType !== "pattern"}
                      onValueChange={(v) =>
                        update({
                          monthlyPattern: {
                            ...config.monthlyPattern,
                            week: v as WeekLabel,
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-28 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEK_LABELS.map((w) => (
                          <SelectItem key={w.value} value={w.value}>
                            {w.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={config.monthlyPattern.day}
                      disabled={config.monthlyType !== "pattern"}
                      onValueChange={(v) =>
                        update({
                          monthlyPattern: {
                            ...config.monthlyPattern,
                            day: v,
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-32 h-8 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FULL_DAYS.map((d) => (
                          <SelectItem key={d} value={d}>
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </label>
                </div>
              </div>
            )}

            {/* ── End Condition ── */}
            <div>
              <p className="text-sm mb-2">Ends</p>
              <div className="flex flex-col gap-3 items-start">

                {/* After N occurrences */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="end-condition"
                    checked={config.endCondition === "after"}
                    onChange={() => update({ endCondition: "after" })}
                    className="accent-primary"
                  />
                  <span className="text-sm">After</span>
                  <Input
                    type="number"
                    min={1}
                    disabled={config.endCondition !== "after"}
                    value={config.occurrences}
                    onChange={(e) =>
                      update({
                        occurrences: Math.max(1, Number(e.target.value)),
                      })
                    }
                    className="w-20 h-8 text-sm"
                  />
                  <span className="text-sm text-muted-foreground">
                    occurrence(s)
                  </span>
                </label>

                {/* On date */}
               <label className="flex items-center gap-3 cursor-pointer flex-wrap">
                  <input
                    type="radio"
                    name="end-condition"
                    checked={config.endCondition === "on_date"}
                    onChange={() => update({ endCondition: "on_date" })}
                    className="accent-primary"
                  />
                  <span className="text-sm">On date</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={config.endCondition !== "on_date"}
                        className={cn(
                          "h-8 text-sm justify-start font-normal",
                          !config.endDate && "text-muted-foreground"
                        )}
                      >
                        {config.endDate
                          ? format(new Date(config.endDate), "dd-MM-yyyy")
                          : "Pick a date"}
                        <CalendarIcon className="ml-2 h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        captionLayout="dropdown-buttons"
                        fromYear={new Date().getFullYear()}
                        toYear={new Date().getFullYear() + 20}
                        selected={
                          config.endDate
                            ? new Date(config.endDate)
                            : undefined
                        }
                        defaultMonth={startDate ? new Date(startDate) : new Date()}
                        onSelect={(d) =>
                          d &&
                          update({ endDate: format(d, "yyyy-MM-dd") })
                        }
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const start = startDate ? new Date(startDate) : today;
                          start.setHours(0, 0, 0, 0);
                          return d < start;
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </label>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};

export default RecurrenceSelector;